/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import {
    Button,
    Heading,
    FlexCol,
    FlexRow,
    InventoryTag,
    FormattedDateTime,
    TzTimeZone,
} from 'sarsaparilla';
import { differenceInDays, startOfDay, endOfDay } from 'date-fns';
import {
    DATE_FORMAT_LONG_WITH_DAYOFWEEK,
    stringToDate,
} from 'ui-permit/shared-components/utils/dateUtils';
import LotteryImage from './LotteryImage';
import { Lottery } from '../types/lottery';

interface UpcomingLotteryProps {
    lottery: Lottery;
}

export default function UpcomingLottery({ lottery }: UpcomingLotteryProps) {
    const daysDiff = differenceInDays(
        endOfDay(new Date(lottery.open_at)),
        startOfDay(new Date())
    );

    const goViewDetails = () => {
        let path;

        switch (lottery.inventory_info.facility_type) {
            case 'permit':
                path = `/permits/${lottery.inventory_id}`;
                break;
            default:
                path = lottery.inventory_info.details_url;
        }

        if (path) {
            window.location.assign(path);
        }
    };

    const lotteryInventoryTypeFunc = () => {
        switch (lottery.inventory_info.facility_type) {
            case 'permit':
                return <InventoryTag.InventoryPermit hasText isSpanItem />;
            case 'ticket':
                return <InventoryTag.InventoryTicket hasText isSpanItem />;
            case 'campground':
            case 'camping':
                return <InventoryTag.InventoryCamping hasText isSpanItem />;
            case 'lottery':
                return <InventoryTag.InventoryLottery hasText isSpanItem />;
            default:
                return null;
        }
    };

    const lotteryDaysUntilOpenFunc = () => {
        switch (daysDiff) {
            case 1:
            case 2:
            case 3:
                return 'lottery-opening-less-than-three-days';
            default:
                return '';
        }
    };

    const lotteryInventoryTypeConst = lotteryInventoryTypeFunc();

    const lotteryDaysUntilOpenConst = lotteryDaysUntilOpenFunc();

    //Attempts to grab facility timezone and if not uses UTC for date/time
    let facilityTimezoneWithDefaultUTC = lottery?.inventory_info?.time_zone ?? '';

    if (facilityTimezoneWithDefaultUTC === '') {
        facilityTimezoneWithDefaultUTC = 'UTC';
    }

    return (
        <FlexCol xs={12} className="lottery-upcoming-card-wrap">
            <div className="lottery-card-image-wrap">
                <LotteryImage lottery={lottery} />
            </div>
            <FlexRow className="lottery-card-body-wrap" alignItems="start">
                <FlexCol xs={12} className="lottery-card-top-wrap">
                    {lotteryInventoryTypeConst}

                    <div className={`lottery-opening-tag ${lotteryDaysUntilOpenConst}`}>
                        Opening in: {daysDiff} {daysDiff === 1 ? 'day' : 'days'}
                    </div>
                </FlexCol>

                <FlexCol md={9} xl={10} className="rec-card-left">
                    <Heading headingLevel={3} appearance="h5" className="mb-half">
                        {lottery.name}
                    </Heading>

                    <div className="lottery-information-wrap">
                        {lottery.inventory_info &&
                            lottery.inventory_info.facility_name && (
                                <div className="lottery-card-info-item">
                                    Part of {lottery.inventory_info.facility_name}
                                </div>
                            )}

                        {lottery.inventory_info && lottery.inventory_info.dates && (
                            <>
                                <div className="lottery-card-title">Event Date</div>
                                <div className="lottery-card-info-item">
                                    <FormattedDateTime
                                        date={stringToDate(
                                            lottery.inventory_info.dates.start
                                        )}
                                        options={{
                                            ...DATE_FORMAT_LONG_WITH_DAYOFWEEK,
                                            timeZone: facilityTimezoneWithDefaultUTC,
                                        }}
                                    />
                                    &nbsp;to&nbsp;
                                    <FormattedDateTime
                                        date={stringToDate(
                                            lottery.inventory_info.dates.end
                                        )}
                                        options={{
                                            ...DATE_FORMAT_LONG_WITH_DAYOFWEEK,
                                            timeZone: facilityTimezoneWithDefaultUTC,
                                        }}
                                    />
                                </div>
                            </>
                        )}

                        <div className="lottery-card-title">Application Opens</div>
                        <div className="lottery-card-info-item">
                            <FormattedDateTime
                                date={stringToDate(lottery.open_at)}
                                format="llll"
                                timeZone={facilityTimezoneWithDefaultUTC as TzTimeZone}
                                showTimeZone
                            />
                        </div>

                        <div className="lottery-card-title">Application Deadline</div>
                        <div className="lottery-card-info-item">
                            <FormattedDateTime
                                date={stringToDate(lottery.close_at)}
                                format="llll"
                                timeZone={facilityTimezoneWithDefaultUTC as TzTimeZone}
                                showTimeZone
                            />
                        </div>

                        <div className="lottery-card-title">
                            Lottery Results Available
                        </div>
                        <div className="lottery-card-info-item">
                            <FormattedDateTime
                                date={stringToDate(lottery.announced_at)}
                                options={{
                                    ...DATE_FORMAT_LONG_WITH_DAYOFWEEK,
                                    timeZone: facilityTimezoneWithDefaultUTC,
                                }}
                            />
                        </div>
                    </div>
                </FlexCol>
                <FlexCol md={3} xl={2} alignSelf="stretch" className="lottery-card-right">
                    <Button
                        data-test-hook="View Details"
                        appearance="tertiary"
                        shouldFitContainer
                        onClick={goViewDetails}
                        aria-label={`View Details for ${lottery.name}`}
                        gaTrackingId="179522022248"
                        gaTrackingValue={lottery.name}
                    >
                        Show Details
                    </Button>
                </FlexCol>
            </FlexRow>
        </FlexCol>
    );
}
